import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfileForm, type ProfileFormValues } from '../components/profile/ProfileForm';
import { useAuth } from '../hooks/useAuth';
import { updateMyProfile } from '../services/usersApi';

/**
 * Editable profile tab. Shows the account summary (name, email, avatar,
 * admin badge if applicable) up top, then the same form the first-time
 * setup page uses below for the editable fields.
 */
export function Profile() {
  const { appUser, setAppUser } = useAuth();
  const navigate = useNavigate();
  const [savedAt, setSavedAt] = useState<number | null>(null);

  if (appUser === null) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="font-mono text-sm text-wca-gray-mid">Loading profile…</span>
      </div>
    );
  }

  const handleSubmit = async (values: ProfileFormValues): Promise<void> => {
    const updated = await updateMyProfile({
      displayName: values.displayName,
      roomNumber: values.roomNumber,
      allergies: values.allergies,
    });
    setAppUser(updated);
    setSavedAt(Date.now());
  };

  const initial: AvatarInitial = getInitial(appUser.displayName, appUser.email);
  const isAdmin = appUser.role === 'admin';
  const memberSince = new Date(appUser.createdAt).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  return (
    <section className="mx-auto max-w-xl px-6 py-10 space-y-6">
      <div className="bg-wca-surface rounded-card shadow-card p-7">
        <div className="flex items-center gap-4">
          {appUser.photoURL !== null ? (
            <img
              src={appUser.photoURL}
              alt=""
              className="h-16 w-16 rounded-full object-cover ring-1 ring-wca-gray-light"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-wca-red-light text-xl font-display text-wca-red">
              {initial}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-2xl truncate">{appUser.displayName}</h1>
            <p className="text-sm text-wca-gray-mid truncate">{appUser.email}</p>
            {isAdmin && (
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.25em] text-wca-gold">
                Admin account
              </p>
            )}
          </div>
        </div>

        <dl className="mt-6 grid grid-cols-2 gap-4 border-t border-wca-gray-light pt-5 text-sm">
          <div>
            <dt className="text-xs text-wca-gray-mid uppercase tracking-wider">Room</dt>
            <dd className="mt-0.5 font-medium">
              {appUser.roomNumber !== null && appUser.roomNumber.length > 0
                ? appUser.roomNumber
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-wca-gray-mid uppercase tracking-wider">Favorites</dt>
            <dd className="mt-0.5 font-medium">{appUser.favoriteItemIds.length}</dd>
          </div>
          <div className="col-span-2">
            <dt className="text-xs text-wca-gray-mid uppercase tracking-wider">Member since</dt>
            <dd className="mt-0.5 font-medium">{memberSince}</dd>
          </div>
        </dl>
      </div>

      <div className="bg-wca-surface rounded-card shadow-card p-7">
        <h2 className="font-display text-xl mb-4">Edit your details</h2>

        {savedAt !== null && (
          <p
            key={savedAt}
            className="mb-4 rounded-lg bg-wca-gold/15 px-3 py-2 text-sm text-wca-black"
          >
            Saved.
          </p>
        )}

        <ProfileForm
          initial={{
            displayName: appUser.displayName,
            roomNumber: appUser.roomNumber ?? '',
            allergies: appUser.allergies,
          }}
          submitLabel="Save changes"
          onSubmit={handleSubmit}
          onCancel={() => navigate(-1)}
        />
      </div>
    </section>
  );
}

type AvatarInitial = string;

function getInitial(displayName: string, email: string): AvatarInitial {
  const source = displayName.trim().length > 0 ? displayName.trim() : email;
  return source.charAt(0).toUpperCase();
}
